<template>
	<div class="chat-history-item " :class="active ? 'active' : ''">
		<div class="message-normal">
			<div class="avatar">
				<head-image :name="showName" :size="38" :url="headImage" :id="msgInfo.sendId"></head-image>
			</div>
			<div class="content">
				<div class="top">
					<span class="sender">{{ showName }}</span>
					<span class="time">{{ $date.toTimeText(msgInfo.sendTime) }}</span>
				</div>
				<div class="bottom">
					<div ref="chatMsgBox" class="message-content-wrapper">
							<span class="message-text" v-if="msgInfo.type == $enums.MESSAGE_TYPE.TEXT"
								v-html="htmlText"></span>
							<span class="message-text" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.MESSAGE_EDITED"
								v-html="htmlText"></span>
							<span class="message-text" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.TIP_TEXT">
								{{ $msgUtil.displayContent(msgInfo.content) }}</span>
						<span class="message-text" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.TIP_TIME">
							{{ $date.toTimeText(msgInfo.sendTime) }}</span>
						<div class="message-image" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.IMAGE">
							<div class="media-shell" v-loading="sending" element-loading-text="发送中.."
								element-loading-background="rgba(0, 0, 0, 0.4)">
								<asset-image class="send-image" :src="contentData.thumbUrl" :strict="true"
									@click.native="showFullImageBox()" loading="lazy" />
							</div>
						</div>
						<div class="message-image" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.STICKER">
							<div class="media-shell" v-loading="sending" element-loading-text="发送中.."
								element-loading-background="rgba(0, 0, 0, 0.4)">
								<asset-image class="send-image" :src="contentData.imageUrl" @click.native="showFullSticker" loading="lazy" />
							</div>
						</div>
						<inline-video-player
							v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.VIDEO"
							variant="history"
							:source="videoSource"
							:poster="videoPoster"
							:loading="videoPreparing"
							:load-error="videoLoadError"
							:previewable="canPreviewVideo"
							:duration="contentData.duration"
							:content-width="contentData.width"
							:content-height="contentData.height"
							:max-width="220"
							:max-height="168"
							:min-width="112"
							:fallback-aspect-ratio="videoAspectRatio"
							@request-load="onPlayVideo"
							@request-preview="onPreviewVideo"
							@error="onVideoLoadError"
						/>
						<div class="message-file" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.FILE">
							<div class="chat-file-box" v-loading="sending">
								<div class="chat-file-info">
									<div class="chat-file-name" @click.stop="onPreviewFile">{{ contentData.name
									}}</div>
									<div class="chat-file-meta">
										<div class="chat-file-type-tag" :class="`chat-file-type-tag--${fileTypeMeta.tone}`">{{ fileTypeMeta.fullLabel }}</div>
										<div class="chat-file-size">{{ fileSize }}</div>
									</div>
								</div>
								<div class="chat-file-icon" :class="`chat-file-icon--${fileTypeMeta.tone}`">
									<span class="chat-file-icon__label">{{ fileTypeMeta.shortLabel }}</span>
								</div>
							</div>
						</div>
						<chat-user-card v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.USER_CARD"
							:cardInfo="contentData"></chat-user-card>
						<chat-group-card v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.GROUP_CARD"
							:cardInfo="contentData" :sendTime="msgInfo.sendTime"></chat-group-card>
						<div class="message-voice" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.AUDIO">
							<button
								type="button"
								class="voice-chip"
								:class="{
									'voice-chip--playing': audioPlaying,
									'voice-chip--loading': audioPreparing
								}"
								:title="audioControlTitle"
								@click.stop="onPlayVoice"
							>
								<span class="voice-chip__icon">
									<i :class="audioControlIcon"></i>
								</span>
								<span class="voice-chip__wave" aria-hidden="true">
									<span class="voice-chip__bar"></span>
									<span class="voice-chip__bar"></span>
									<span class="voice-chip__bar"></span>
									<span class="voice-chip__bar"></span>
								</span>
								<span class="voice-chip__duration">{{ audioDurationText }}</span>
							</button>
						</div>
						<div class="message-text" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.MERGE_FORWARD">
							[合并转发2] 共{{ JSON.parse(msgInfo.content).messages.length }}条消息
						</div>
						<div class="message-text" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.ACT_RT_VOICE">
							[语音通话]
						</div>
						<div class="message-text" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.ACT_RT_VIDEO">
							[视频通话]
						</div>
						<div v-else class="message-text">[暂不支持该消息类型]</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import AssetImage from "../common/AssetImage.vue";
import ChatUserCard from "./ChatUserCard.vue";
import ChatGroupCard from "./ChatGroupCard.vue";
import InlineVideoPlayer from "./InlineVideoPlayer.vue";
import { getFileExtension, resolveFileTypeMeta } from "../../utils/fileTypeMeta";

export default {
	name: "chatHistoryItem",
	components: {
		HeadImage,
		AssetImage,
		ChatUserCard,
		ChatGroupCard,
		InlineVideoPlayer
	},
	props: {
		active: {
			type: Boolean,
			default: false
		},
		headImage: {
			type: String,
			default: ''
		},
		showName: {
			type: String,
			required: true
		},
		quoteShowName: {
			type: String,
			default: ''
		},
		msgInfo: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			resolvedVideoUrl: '',
			resolvedVideoPoster: '',
			resolvedAudioUrl: '',
			videoPreparing: false,
			videoLoadError: false,
			audioPreparing: false,
			audioPlaying: false,
			mediaRequestId: 0
		}
	},
	methods: {
		showAssetError(message) {
			this.$message.error(message || '资源暂不可用');
		},
		hasAssetSource(source) {
			return this.$assetResolver.hasAssetSource(source);
		},
		async syncMediaAssets() {
			const requestId = ++this.mediaRequestId;
			this.resetAudioState();
			this.resolvedVideoUrl = '';
			this.resolvedVideoPoster = '';
			this.resolvedAudioUrl = '';
			this.videoPreparing = false;
			this.videoLoadError = false;
			if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO) {
				this.resolvedVideoPoster = await this.resolveMediaUrl(this.contentData.coverUrl, requestId, {
					kind: 'cover'
				});
			}
		},
		async resolveMediaUrl(source, requestId, extra = {}, notifyError = false) {
			if (!source) {
				return '';
			}
			if (!this.hasAssetSource(source)) {
				if (notifyError && requestId === this.mediaRequestId) {
					this.showAssetError('资源暂不可用');
				}
				return '';
			}
			try {
				const url = await this.$assetResolver.resolveUrl(source, extra);
				if (requestId !== this.mediaRequestId) {
					return '';
				}
				return url;
			} catch (error) {
				if (notifyError && requestId === this.mediaRequestId) {
					this.showAssetError(error && error.message ? error.message : '资源加载失败');
				}
				return '';
			}
		},
		async onPlayVideo(event) {
			if (this.resolvedVideoUrl || this.videoPreparing) {
				return;
			}
			if (event && typeof event.preventDefault === 'function') {
				event.preventDefault();
			}
			const requestId = this.mediaRequestId;
			this.videoPreparing = true;
			this.videoLoadError = false;
			const videoUrl = await this.resolveMediaUrl(this.contentData.videoUrl, requestId, {
				kind: 'video'
			}, true);
			if (!videoUrl || requestId !== this.mediaRequestId) {
				if (requestId === this.mediaRequestId) {
					this.videoPreparing = false;
					this.videoLoadError = true;
				}
				return;
			}
			this.resolvedVideoUrl = videoUrl;
			if (!this.resolvedVideoPoster) {
				this.resolvedVideoPoster = await this.resolveMediaUrl(this.contentData.coverUrl, requestId, {
					kind: 'cover'
				});
			}
			if (requestId === this.mediaRequestId) {
				this.videoPreparing = false;
				this.videoLoadError = false;
			}
		},
		onVideoLoadError() {
			if (!this.resolvedVideoUrl) {
				return;
			}
			this.resolvedVideoUrl = '';
			this.videoPreparing = false;
			this.videoLoadError = true;
			this.showAssetError('资源加载失败，请重试');
		},
		showFullImageBox() {
			let imageUrl = this.imagePreviewSource;
			if (imageUrl) {
				this.$eventBus.$emit("openFullImage", {
					url: imageUrl,
					strict: true,
					kind: 'image',
					fileName: this.imagePreviewMeta.fileName,
					contentType: this.imagePreviewMeta.contentType
				});
			}
		},
		showFullSticker() {
			const imageUrl = this.contentData.imageUrl;
			if (imageUrl) {
				this.$eventBus.$emit("openFullImage", imageUrl);
			}
		},
		onPreviewVideo() {
			if (!this.contentData.videoUrl) {
				this.showAssetError('视频暂不可用');
				return;
			}
			this.$eventBus.$emit("openFullVideo", {
				url: this.contentData.videoUrl,
				poster: this.contentData.coverUrl || '',
				strict: true
			});
		},
		ensureAudioPlayer() {
			if (this.audio) {
				return;
			}
			const player = new Audio();
			player.preload = 'auto';
			player.onplay = () => {
				this.audioPreparing = false;
				this.audioPlaying = true;
			};
			player.onpause = () => {
				this.audioPreparing = false;
				this.audioPlaying = false;
			};
			player.onended = () => {
				this.audioPreparing = false;
				this.audioPlaying = false;
			};
			player.onerror = () => {
				this.onAudioLoadError();
			};
			this.audio = player;
		},
		resetAudioState() {
			this.audioPreparing = false;
			this.audioPlaying = false;
			if (!this.audio) {
				return;
			}
			this.audio.pause();
			this.audio.removeAttribute('src');
			if (typeof this.audio.load === 'function') {
				this.audio.load();
			}
		},
		async onPlayVoice(event) {
			if (event && typeof event.preventDefault === 'function') {
				event.preventDefault();
			}
			this.ensureAudioPlayer();
			if (this.audioPlaying) {
				this.audio.pause();
				return;
			}
			const requestId = this.mediaRequestId;
			this.audioPreparing = true;
			const audioUrl = this.resolvedAudioUrl || await this.resolveMediaUrl(this.contentData.url, requestId, {
				kind: 'audio'
			}, true);
			if (!audioUrl || requestId !== this.mediaRequestId) {
				if (requestId === this.mediaRequestId) {
					this.audioPreparing = false;
				}
				return;
			}
			this.resolvedAudioUrl = audioUrl;
			if (this.audio.src !== audioUrl) {
				this.audio.src = audioUrl;
				if (typeof this.audio.load === 'function') {
					this.audio.load();
				}
			}
			this.audio.play().catch(() => {
				this.audioPreparing = false;
				this.audioPlaying = false;
				this.showAssetError('资源加载失败，请重试');
			});
		},
		onAudioLoadError() {
			const hasResolvedAudio = Boolean(this.resolvedAudioUrl || (this.audio && this.audio.src));
			this.resolvedAudioUrl = '';
			this.resetAudioState();
			if (hasResolvedAudio) {
				this.showAssetError('资源加载失败，请重试');
			}
		},
		onPreviewFile() {
			if (!this.contentData.url) {
				this.showAssetError('文件暂不可用');
				return;
			}
			this.$eventBus.$emit("openPreviewFile", {
				url: this.contentData.url,
				fileName: this.contentData.name,
				strict: true
			});
		}
	},
	computed: {
		sending() {
			return this.msgInfo.status == this.$enums.MESSAGE_STATUS.SENDING;
		},
		contentData() {
			return JSON.parse(this.msgInfo.content)
		},
		imagePreviewSource() {
			return this.contentData.originUrl || this.contentData.thumbUrl || '';
		},
		imagePreviewMeta() {
			return {
				fileName: this.contentData.name || this.contentData.fileName || 'image',
				contentType: this.contentData.contentType || this.contentData.mimeType || 'image/*'
			};
		},
		videoSource() {
			return this.resolvedVideoUrl || undefined;
		},
		videoPoster() {
			return this.resolvedVideoPoster || undefined;
		},
		videoAspectRatio() {
			const width = Number(this.contentData.width) || 0;
			const height = Number(this.contentData.height) || 0;
			if (!width || !height) {
				return '16 / 9';
			}
			return `${Math.max(1, width)} / ${Math.max(1, height)}`;
		},
		canPreviewVideo() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO && Boolean(this.contentData.videoUrl);
		},
		audioSource() {
			return this.resolvedAudioUrl || undefined;
		},
		audioDurationText() {
			const duration = Number(this.contentData.duration);
			if (!Number.isFinite(duration) || duration <= 0) {
				return '语音';
			}
			return `${Math.max(1, Math.round(duration))}"`;
		},
		audioControlIcon() {
			if (this.audioPreparing) {
				return 'el-icon-loading';
			}
			return this.audioPlaying ? 'el-icon-video-pause' : 'el-icon-caret-right';
		},
		audioControlTitle() {
			if (this.audioPreparing) {
				return '语音加载中';
			}
			return this.audioPlaying ? '暂停播放' : '播放语音';
		},
		fileSize() {
			let size = this.contentData.size;
			if (size > 1024 * 1024) {
				return Math.round(size / 1024 / 1024) + "M";
			}
			if (size > 1024) {
				return Math.round(size / 1024) + "KB";
			}
			return size + "B";
		},
		fileTypeMeta() {
			return resolveFileTypeMeta(getFileExtension(this.contentData.name));
		},
		htmlText() {
			let text = this.$str.html2Escape(this.msgInfo.content)
			text = this.$url.replaceURLWithHTMLLinks(text, '')
			return this.$emo.transform(text, 'emoji-normal')
		}
	},
	watch: {
		'msgInfo.content': {
			immediate: true,
			handler() {
				this.syncMediaAssets();
			}
		}
	},
	beforeDestroy() {
		this.mediaRequestId += 1;
		if (this.audio) {
			this.audio.pause();
			this.audio = null;
		}
	}
}
</script>

<style lang="scss" scoped>
.chat-history-item {
	width: 100%;
	padding: 8px 10px;
	cursor: pointer;
	border-radius: 8px;
	box-sizing: border-box;

	&:hover {
		background: var(--im-background-active);
	}

	&.active {
		background: var(--im-background-active-dark);
	}

	.message-normal {
		position: relative;
		font-size: 0;
		padding-left: 50px;
		min-height: 44px;
		margin: 0;

		.avatar {
			position: absolute;
			width: 40px;
			height: 40px;
			top: 0;
			left: 0;
		}

		.content {
			text-align: left;
			min-width: 0;
			padding-top: 1px;

			.top {
				display: flex;
				align-items: center;
				justify-content: space-between;
				gap: 10px;
				color: var(--im-text-color-light);
				font-size: 12px;
				line-height: 18px;
				margin-bottom: 4px;

				.sender {
					flex: 1;
					min-width: 0;
					color: var(--im-text-color);
					font-weight: 500;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				.time {
					flex-shrink: 0;
				}
			}

			.bottom {
				padding-right: 0;
				margin-top: 0;
				min-width: 0;

				.message-content-wrapper {
					display: block;
					width: fit-content;
					max-width: 100%;
					min-width: 0;
				}

				.message-text {
					display: inline-block;
					position: relative;
					max-width: 100%;
					line-height: 24px;
					font-size: 13px;
					text-align: left;
					white-space: pre-wrap;
					word-break: break-word;
				}

				.message-image,
				.message-video {
					display: inline-flex;
					width: fit-content;
					flex-wrap: nowrap;
					flex-direction: row;
					align-items: center;
					max-width: 100%;
					padding-top: 2px;

					.media-shell {
						display: inline-flex;
						width: fit-content;
						max-width: 100%;
						line-height: 0;
					}

					.send-image {
						display: block;
						width: auto;
						min-width: 112px;
						min-height: 84px;
						max-width: min(220px, 100%);
						max-height: 168px;
						border-radius: 10px;
						background: rgba(var(--im-black-rgb), 0.04);
						object-fit: cover;
						cursor: pointer;
					}

					.send-video {
						width: auto;
						min-width: 112px;
						min-height: 84px;
						max-width: min(220px, 100%);
						max-height: 168px;
						border-radius: 10px;
						background: rgba(var(--im-black-rgb), 0.04);
						overflow: hidden;
						object-fit: cover;
					}

					.video-shell {
						position: relative;
						display: inline-flex;
						width: fit-content;
						align-items: center;
						justify-content: center;
					}

					.video-poster--empty {
						background: linear-gradient(135deg, rgba(var(--im-black-rgb), 0.28), rgba(var(--im-black-rgb), 0.12));
					}

					.video-mask {
						position: absolute;
						inset: 0;
						display: flex;
						align-items: center;
						justify-content: center;
						gap: 6px;
						background: rgba(var(--im-black-rgb), 0.18);
						color: #fff;
						font-size: 22px;
						border-radius: 10px;
					}

					.video-mask--loading {
						font-size: 12px;
						background: rgba(var(--im-black-rgb), 0.32);
					}
				}

				.message-file {
					display: flex;
					flex-wrap: nowrap;
					flex-direction: row;
					align-items: center;
					margin-bottom: 2px;

					.chat-file-box {
						display: flex;
						flex-wrap: nowrap;
						align-items: center;
						border: 1px solid var(--im-control-border);
						background: var(--im-control-soft-bg);
						border-radius: 8px;
						padding: 10px 12px;
						width: 100%;
						min-width: 0;

						.chat-file-info {
							flex: 1;
							min-width: 0;
							height: 100%;
							text-align: left;
							font-size: 13px;
							margin-right: 8px;

							.chat-file-name {
								display: -webkit-box;
								width: 100%;
								min-width: 0;
								max-width: 100%;
								font-size: 13px;
								margin-bottom: 3px;
								overflow: hidden;
								-webkit-box-orient: vertical;
								-webkit-line-clamp: 2;
								word-break: break-all;
								color: var(--im-color-primary);
							}

							.chat-file-meta {
								display: flex;
								align-items: center;
								gap: 6px;
								flex-wrap: wrap;
							}

							.chat-file-type-tag,
							.chat-file-size {
								display: inline-flex;
								align-items: center;
								height: 18px;
								padding: 0 6px;
								border-radius: 999px;
								font-size: var(--im-font-size-smaller);
								font-weight: 500;
							}

							.chat-file-size {
								background: rgba(var(--im-black-rgb), 0.04);
								color: var(--im-text-color-light);
							}

							.chat-file-type-tag {
								background: rgba(var(--im-black-rgb), 0.06);
								color: var(--im-text-color-secondary);
							}

							.chat-file-type-tag--primary {
								background: rgba(var(--im-primary-rgb), 0.1);
								color: var(--im-color-primary);
							}

							.chat-file-type-tag--success {
								background: rgba(var(--im-success-rgb, 103, 194, 58), 0.12);
								color: var(--im-color-success, #67c23a);
							}

							.chat-file-type-tag--warning {
								background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.12);
								color: var(--im-color-warning, #e6a23c);
							}

							.chat-file-type-tag--danger {
								background: rgba(var(--im-danger-rgb), 0.1);
								color: var(--im-color-danger);
							}

							.chat-file-type-tag--teal {
								background: rgba(20, 184, 166, 0.12);
								color: #0f766e;
							}
						}

						.chat-file-icon {
							display: inline-flex;
							align-items: center;
							justify-content: center;
							flex-shrink: 0;
							width: 34px;
							height: 34px;
							border-radius: 10px;
							background: rgba(var(--im-black-rgb), 0.05);
							border: 1px solid rgba(var(--im-black-rgb), 0.06);

							.chat-file-icon__label {
								font-size: 11px;
								font-weight: 700;
								letter-spacing: 0.4px;
								color: var(--im-text-color-secondary);
								flex-shrink: 0;
							}
						}

						.chat-file-icon--primary {
							background: rgba(var(--im-primary-rgb), 0.08);
							border-color: rgba(var(--im-primary-rgb), 0.12);
						}

						.chat-file-icon--primary .chat-file-icon__label {
							color: var(--im-color-primary);
						}

						.chat-file-icon--success {
							background: rgba(var(--im-success-rgb, 103, 194, 58), 0.1);
							border-color: rgba(var(--im-success-rgb, 103, 194, 58), 0.14);
						}

						.chat-file-icon--success .chat-file-icon__label {
							color: var(--im-color-success, #67c23a);
						}

						.chat-file-icon--warning {
							background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.1);
							border-color: rgba(var(--im-warning-rgb, 230, 162, 60), 0.14);
						}

						.chat-file-icon--warning .chat-file-icon__label {
							color: var(--im-color-warning, #e6a23c);
						}

						.chat-file-icon--danger {
							background: rgba(var(--im-danger-rgb), 0.08);
							border-color: rgba(var(--im-danger-rgb), 0.12);
						}

						.chat-file-icon--danger .chat-file-icon__label {
							color: var(--im-color-danger);
						}

						.chat-file-icon--teal {
							background: rgba(20, 184, 166, 0.1);
							border-color: rgba(20, 184, 166, 0.14);
						}

						.chat-file-icon--teal .chat-file-icon__label {
							color: #0f766e;
						}
					}
				}

				.message-voice {
					display: inline-flex;
					max-width: 100%;

					.voice-chip {
						display: inline-flex;
						align-items: center;
						gap: 10px;
						min-width: 132px;
						max-width: 100%;
						min-height: 40px;
						padding: 0 12px;
						border: 1px solid var(--im-control-border);
						border-radius: 999px;
						background: var(--im-control-bg);
						color: var(--im-text-color);
						cursor: pointer;
						transition: border-color 0.2s ease, background-color 0.2s ease;

						&:hover {
							border-color: rgba(var(--im-primary-rgb), 0.18);
							background: rgba(var(--im-primary-rgb), 0.04);
						}
					}

					.voice-chip__icon {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						width: 26px;
						height: 26px;
						border-radius: 999px;
						background: rgba(var(--im-black-rgb), 0.06);
						color: var(--im-color-primary);
						flex-shrink: 0;
					}

					.voice-chip__wave {
						display: inline-flex;
						align-items: flex-end;
						gap: 3px;
						height: 14px;
						flex: 1;
						min-width: 0;
					}

					.voice-chip__bar {
						width: 3px;
						border-radius: 999px;
						background: currentColor;
						opacity: 0.42;
						transform-origin: center bottom;
					}

					.voice-chip__bar:nth-child(1) {
						height: 6px;
					}

					.voice-chip__bar:nth-child(2) {
						height: 10px;
					}

					.voice-chip__bar:nth-child(3) {
						height: 8px;
					}

					.voice-chip__bar:nth-child(4) {
						height: 12px;
					}

					.voice-chip__duration {
						flex-shrink: 0;
						font-size: 12px;
						font-weight: 600;
						color: var(--im-text-color-light);
					}

					.voice-chip--playing .voice-chip__bar {
						opacity: 0.88;
						animation: voice-chip-wave 1.05s ease-in-out infinite;
					}

					.voice-chip--playing .voice-chip__bar:nth-child(2) {
						animation-delay: 0.08s;
					}

					.voice-chip--playing .voice-chip__bar:nth-child(3) {
						animation-delay: 0.16s;
					}

					.voice-chip--playing .voice-chip__bar:nth-child(4) {
						animation-delay: 0.24s;
					}
				}
			}
		}
	}
}

@keyframes voice-chip-wave {
	0%,
	100% {
		transform: scaleY(0.55);
	}

	50% {
		transform: scaleY(1);
	}
}

	@media (max-width: 640px) {
	.chat-history-item {
		padding: 8px 6px;

		.message-normal {
			padding-left: 46px;

			.avatar {
				width: 36px;
				height: 36px;
			}

			.content {
				.bottom {
					.message-image,
					.message-video {
						.send-image,
						.send-video {
							max-width: min(176px, 100%);
							max-height: 132px;
						}
					}
				}
			}
		}
	}
}
</style>
